import CreatePersonModal from '@/components/features/People/forms/CreatePersonModal';
import { SheetPickerList } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useTranslations } from '@/hooks/useTranslations';
import axios from '@/lib/axios';
import { useCan } from '@/lib/utils';
import { People } from '@/types';
import type { AssignPersonToCompanyProps, PersonWithCompany } from '@/types/forms/companies';
import { router } from '@inertiajs/react';
import { Plus, User as UserIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

// Helper function to check if an error is a cancelled request
function isCancelledRequest(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    return (
        ('code' in error && error.code === 'ECONNABORTED') ||
        ('isCancelled' in error && error.isCancelled === true) ||
        ('message' in error && (error.message === 'Request aborted' || String(error.message).includes('canceled')))
    );
}

export default function AssignPersonToCompany({
    company,
    people: initialPeople = [],
    companyPeople: initialCompanyPeople = [],
    trigger,
    open: openProp,
    onOpenChange,
}: AssignPersonToCompanyProps) {
    const { t } = useTranslations();
    const { can } = useCan();
    /** Api\PersonController::store() rejects without this, so offering the form would
        walk the user through a sheet they can never save. */
    const canCreateContacts = can('contacts.create');
    const [isOpen, setIsOpen] = useControllableOpen(openProp, onOpenChange);
    const [personSearch, setPersonSearch] = useState('');
    const [people, setPeople] = useState<People[]>(initialPeople || []);
    const [companyPeople, setCompanyPeople] = useState<People[]>(initialCompanyPeople || []);
    const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>((initialCompanyPeople || []).map((p: People) => p.id));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [createPersonOpen, setCreatePersonOpen] = useState(false);

    useEffect(() => {
        setPeople(initialPeople || []);
        setCompanyPeople(initialCompanyPeople || []);
        setSelectedPersonIds((initialCompanyPeople || []).map((p: People) => p.id));
    }, [initialPeople, initialCompanyPeople]);

    // Cleanup effect for dialog state
    useEffect(() => {
        if (!isOpen) {
            setPersonSearch('');
            setError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchData = async () => {
            if (isOpen) {
                setLoading(true);
                setError(null);
                try {
                    // Fetch initial people (first 15) and company people data
                    const [peopleResponse, companyResponse] = await Promise.all([
                        axios.get('/api/people?limit=15&with_company=true'),
                        axios.get(`/api/companies/${company.id}/people`),
                    ]);

                    // Handle the API response structure correctly
                    const peopleData = Array.isArray(peopleResponse.data.people) ? peopleResponse.data.people : [];
                    const companyPeopleData = Array.isArray(companyResponse.data.people) ? companyResponse.data.people : [];

                    setPeople(peopleData);
                    setCompanyPeople(companyPeopleData);
                    setSelectedPersonIds(companyPeopleData.map((p: People) => p.id));
                } catch (error: unknown) {
                    // Ignore cancelled requests (expected during navigation)
                    if (isCancelledRequest(error)) {
                        return;
                    }
                    console.error('Error fetching data:', error);
                    toast.error(t('companies.forms.assign_person.toasts.failed', 'Failed to load data'));
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [isOpen, company.id]);

    // Search functionality
    useEffect(() => {
        const searchPeople = async () => {
            if (personSearch.trim() && personSearch.length >= 2) {
                setLoading(true);
                try {
                    const response = await axios.get(`/api/people?search=${encodeURIComponent(personSearch.trim())}&limit=50&with_company=true`);
                    const searchResults = Array.isArray(response.data.people) ? response.data.people : [];

                    // Merge search results with company people to ensure they always show up
                    const mergedResults = [...searchResults];

                    // Add company people that aren't in search results
                    companyPeople.forEach((companyPerson: People) => {
                        if (!searchResults.some((p: People) => p.id === companyPerson.id)) {
                            mergedResults.push(companyPerson);
                        }
                    });

                    setPeople(mergedResults);
                } catch (error: unknown) {
                    // Ignore cancelled requests (expected during navigation)
                    if (isCancelledRequest(error)) {
                        return;
                    }
                    console.error('Error searching contacts:', error);
                    toast.error(t('companies.forms.assign_person.toasts.failed', 'Failed to search contacts'));
                } finally {
                    setLoading(false);
                }
            } else if (personSearch.trim() === '') {
                // Reset to initial people when search is cleared
                setLoading(true);
                try {
                    const response = await axios.get('/api/people?limit=15&with_company=true');
                    const initialPeopleData = Array.isArray(response.data.people) ? response.data.people : [];

                    // Merge with company people to ensure they always show up
                    const mergedResults = [...initialPeopleData];

                    companyPeople.forEach((companyPerson: People) => {
                        if (!initialPeopleData.some((p: People) => p.id === companyPerson.id)) {
                            mergedResults.push(companyPerson);
                        }
                    });

                    setPeople(mergedResults);
                } catch (error: unknown) {
                    // Ignore cancelled requests (expected during navigation)
                    if (isCancelledRequest(error)) {
                        return;
                    }
                    console.error('Error fetching initial contacts:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        const debounceTimer = setTimeout(searchPeople, 300);
        return () => clearTimeout(debounceTimer);
    }, [personSearch, companyPeople]);

    // Save assignments on button click
    const handleSave = async () => {
        setSaving(true);
        try {
            // Find people to add and remove (using string IDs)
            const toAdd = selectedPersonIds.filter((id: string) => !companyPeople.some((p: People) => p.id === id));
            const toRemove = companyPeople.filter((p: People) => !selectedPersonIds.includes(p.id)).map((p: People) => p.id);

            // Remove unassigned first
            for (const personId of toRemove) {
                try {
                    await axios.delete(`/api/companies/${company.id}/people/${personId}`);
                } catch (error: unknown) {
                    if (isCancelledRequest(error)) {
                        return;
                    }
                    console.error(`Error removing contact ${personId}:`, error);
                    // Continue with other operations even if one fails
                }
            }

            // Add new assignments (handle reassignment from other companies)
            for (const personId of toAdd) {
                try {
                    await axios.post(`/api/companies/${company.id}/people`, { person_id: personId });
                } catch (error: unknown) {
                    if (isCancelledRequest(error)) {
                        return;
                    }

                    // Handle 409 conflict - person is assigned to another company
                    if (axios.isAxiosError && axios.isAxiosError(error) && error.response?.status === 409) {
                        // Find the person to get their current company
                        const person = people.find((p: People) => p.id === personId);
                        if (person?.company?.id && person.company.id !== company.id) {
                            // Remove from old company first, then assign to new one
                            try {
                                await axios.delete(`/api/companies/${person.company.id}/people/${personId}`);
                                // Now assign to new company
                                await axios.post(`/api/companies/${company.id}/people`, { person_id: personId });
                            } catch (reassignError: unknown) {
                                if (isCancelledRequest(reassignError)) {
                                    return;
                                }
                                console.error(`Error reassigning contact ${personId}:`, reassignError);
                                toast.error(
                                    t('companies.forms.assign_person.toasts.reassign_failed', 'Failed to reassign :name from :company')
                                        .replace(':name', `${person.first_name} ${person.last_name}`)
                                        .replace(':company', person.company.name),
                                );
                                throw reassignError;
                            }
                        } else {
                            throw error;
                        }
                    } else {
                        throw error;
                    }
                }
            }

            toast.success(t('companies.forms.assign_person.toasts.updated', 'Contact assignments updated!'));
            setIsOpen(false);
            router.reload();
        } catch (error: unknown) {
            // Ignore cancelled requests (expected during navigation)
            if (isCancelledRequest(error)) {
                return;
            }
            console.error('Error updating contact assignments:', error);
            toast.error(t('companies.forms.assign_person.toasts.failed', 'Failed to update contact assignments'));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Hands off to the create sheet. Both are sheets (drawers on mobile), so the
     * assign one has to finish animating out first — mounting the second while
     * the first is still leaving stacks two overlays and leaves the body with a
     * stuck pointer-events lock.
     */
    const handleCreateContact = () => {
        setIsOpen(false);
        window.setTimeout(() => setCreatePersonOpen(true), 350);
    };

    /**
     * The contact is created with `company_id` already set, so the API assigns
     * it on the spot. Reopening the assign sheet refetches both lists, which
     * brings the new contact back in already selected.
     */
    const handlePersonCreated = () => {
        setCreatePersonOpen(false);
        router.reload();
        window.setTimeout(() => setIsOpen(true), 350);
    };

    // Merge people with company people to ensure assigned people always show up
    const filteredPeople = useMemo(() => {
        const peopleMap = new Map<string, PersonWithCompany>();

        // Add all people from search/initial list
        people.forEach((person: People) => {
            peopleMap.set(person.id, person as PersonWithCompany);
        });

        // Add/update with company people to ensure they're always included
        companyPeople.forEach((companyPerson: People) => {
            const existing = peopleMap.get(companyPerson.id);
            if (existing) {
                // Update with company info if not present
                if (!existing.company && companyPerson.company) {
                    existing.company = companyPerson.company;
                }
            } else {
                // Add company person if not in the list
                peopleMap.set(companyPerson.id, companyPerson as PersonWithCompany);
            }
        });

        return Array.from(peopleMap.values());
    }, [people, companyPeople]);

    return (
        <>
            <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="sheet">
                {trigger ? (
                    <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
                ) : openProp === undefined ? (
                    // Only when the modal owns its open state: a page driving it
                    // supplies its own buttons and must not get a stray one here.
                    <ResponsiveModalTrigger asChild>
                        <Button variant={'outline'}>
                            <UserIcon /> {t('companies.forms.assign_person.buttons.add_contact', 'Add contact')}
                        </Button>
                    </ResponsiveModalTrigger>
                ) : null}
                <ResponsiveModalContent size="lg">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('companies.forms.assign_person.title', 'Contact Assignments')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t(
                                'companies.forms.assign_person.description',
                                'Select or deselect contacts to assign them to this company. Click Save to apply changes.',
                            )}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody>
                        {/* No inner heading: the sheet title already says what this is,
                            so the contacts fill the height instead. */}
                        {error ? <p className="text-destructive pb-3 text-sm">{error}</p> : null}
                        <SheetPickerList
                            multiple
                            values={selectedPersonIds}
                            onValuesChange={setSelectedPersonIds}
                            search={personSearch}
                            onSearchChange={setPersonSearch}
                            loading={loading}
                            options={filteredPeople.map((person: PersonWithCompany) => ({
                                id: person.id,
                                label: `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim(),
                                description: [person.email, person.phone].filter(Boolean).join(' • '),
                                badge: person.company ? (
                                    <Badge variant={person.company.id === company.id ? 'default' : 'secondary'} className="text-xs">
                                        {person.company.id === company.id
                                            ? t('companies.forms.assign_person.messages.current', 'Current')
                                            : person.company.name}
                                    </Badge>
                                ) : null,
                            }))}
                            labels={{
                                search: t('companies.forms.assign_person.placeholders.search_contacts', 'Search contacts...'),
                                empty: t('companies.forms.assign_person.messages.no_contacts_match', 'No contacts match your search.'),
                                loading: t('companies.forms.assign_person.messages.loading_contacts', 'Loading contacts...'),
                            }}
                        />
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter className="md:justify-between">
                        {canCreateContacts ? (
                            <Button variant="outline" onClick={handleCreateContact} disabled={saving}>
                                <Plus /> {t('companies.forms.assign_person.buttons.new_contact', 'New contact')}
                            </Button>
                        ) : (
                            <span />
                        )}
                        <Button onClick={handleSave} disabled={saving}>
                            {saving
                                ? t('companies.forms.assign_person.buttons.saving', 'Saving...')
                                : t('companies.forms.assign_person.buttons.save', 'Save')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>

            <CreatePersonModal
                open={createPersonOpen}
                onOpenChange={setCreatePersonOpen}
                companyId={company.id}
                companyName={company.name}
                onCreated={handlePersonCreated}
            />
        </>
    );
}
